/**
 * Symmetric, zero-based slide index window centered on the currently selected
 * slide. Used by the chat extract path to restrict `RunOfficeExtractor` to a
 * subset of slides rather than the entire deck.
 *
 * Window semantics:
 * - `windowSize` is the total number of slides the window should contain.
 * - The window is centered on `currentIndex`. When `windowSize` is even, the
 *   extra slide goes after the current one (e.g. N=4 around current=4 yields
 *   indices 3..6, not 2..5).
 * - When the window would extend past the start (index < 0) or end
 *   (index >= totalSlides) of the deck, it shifts toward the deck interior
 *   so it still contains `min(windowSize, totalSlides)` slides.
 * - When the deck is smaller than `windowSize`, the whole deck is returned.
 *
 * The returned `end` is inclusive — matching the FactSet
 * `RunOfficeExtractor` `filters.slides.ranges` schema (see
 * `f2_docs/powerpoint/office-extractor/filters/slides.md`) — so the caller
 * can pass `{ start, end }` straight through without conversion.
 *
 * An empty window (no slides selected) is signalled by `end < start`.
 */
export function computeContextSlideWindow(
  currentIndex: number,
  totalSlides: number,
  windowSize: number
): { start: number; end: number } {
  if (totalSlides <= 0 || windowSize <= 0) {
    return { start: 0, end: -1 };
  }
  if (windowSize >= totalSlides) {
    return { start: 0, end: totalSlides - 1 };
  }

  const clampedCurrent = Math.min(Math.max(currentIndex, 0), totalSlides - 1);

  const halfBefore = Math.floor((windowSize - 1) / 2);
  const halfAfter = windowSize - 1 - halfBefore;

  let start = clampedCurrent - halfBefore;
  let end = clampedCurrent + halfAfter;

  if (start < 0) {
    end += -start;
    start = 0;
  }
  if (end >= totalSlides) {
    start -= end - (totalSlides - 1);
    end = totalSlides - 1;
    start = Math.max(0, start);
  }
  return { start, end };
}
